import { FLEET_QUEUE_FLOOR_MS, type GetFleetBoardResponse } from '@immo/api-contract';
import type { NetworkError } from '@/lib/errors/network-error';

/** The backend reads the tracker at most once per 30 s, so a faster poll would only re-read the same snapshot. */
export const FLEET_BOARD_POLL_INTERVAL_MS = FLEET_QUEUE_FLOOR_MS;

/** A snapshot older than two floors means the backend's last tracker reads failed and it is serving the old one. */
export const FLEET_BOARD_STALE_AFTER_MS = FLEET_QUEUE_FLOOR_MS * 2;

/** The answers the page shows as a state of its own; any other failure is a crash. */
export const FLEET_BOARD_PAGE_ERROR_CODES = [
  'fleet.queue.team-not-found',
  'fleet.queue.tracker-unreachable',
  'fleet.queue.tracker-not-configured',
] as const;
export type FleetBoardPageErrorCode = (typeof FLEET_BOARD_PAGE_ERROR_CODES)[number];

export function isFleetBoardPageErrorCode(code: string | undefined): code is FleetBoardPageErrorCode {
  return FLEET_BOARD_PAGE_ERROR_CODES.includes(code as FleetBoardPageErrorCode);
}

export type FleetBoardPoll =
  | { status: 'loading' }
  | { status: 'crashed'; network?: NetworkError }
  | { status: 'failed'; code: FleetBoardPageErrorCode }
  | { status: 'loaded'; board: GetFleetBoardResponse; failure: string | undefined | null };

export type FleetBoardPollAction =
  | { type: 'reset' }
  | { type: 'answered'; board: GetFleetBoardResponse }
  | { type: 'failed'; code: string | undefined; network?: NetworkError };

export const INITIAL_FLEET_BOARD_POLL: FleetBoardPoll = { status: 'loading' };

/**
 * A failed re-read keeps the last board and sets `failure` (`undefined`: an unknown failure); an answer clears it.
 * `reset` is a view switch: the other view's board is not this one's.
 */
export function fleetBoardPollReducer(state: FleetBoardPoll, action: FleetBoardPollAction): FleetBoardPoll {
  if (action.type === 'reset') return INITIAL_FLEET_BOARD_POLL;

  if (action.type === 'answered') return { status: 'loaded', board: action.board, failure: null };

  if (state.status === 'loaded') return { ...state, failure: action.code };

  return isFleetBoardPageErrorCode(action.code)
    ? { status: 'failed', code: action.code }
    : { status: 'crashed', ...(action.network !== undefined ? { network: action.network } : {}) };
}

/** The backend answered, but with a snapshot it could not refresh: Linear is down. */
export function isStaleSnapshot(fetchedAt: string, now: number): boolean {
  return now - Date.parse(fetchedAt) > FLEET_BOARD_STALE_AFTER_MS;
}
